import React, { useCallback, useEffect, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery } from '@material-ui/core'
import useAuthUser from '../hooks/useAuthUser'
import { fetchWorkCalendarUsers, fetchWorkCalendarEntries, saveWorkCalendarEntry, deleteWorkCalendarEntry, fetchWorkCalendarSummary, fetchWorkCalendarActivity } from '../api'
import styles from './WorkCalendarPage.module.css'
import { formatActivityTime, formatActivityDateTime, formatActivityValue } from '../utils/activityTime'

const TIME_ZONE = 'America/New_York'
const adminEmail = (process.env.REACT_APP_ACTIVITY_LOG_ADMIN_EMAIL || 'paul@example.com').trim().toLowerCase()
const dayLabel = type => type === 'half' ? 'Half day' : 'Full day'
const userLabel = user => user.name || user.email || 'Unknown user'
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback
const dateValid = value => /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value
export const easternToday = () => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())
  return ['year', 'month', 'day'].map(type => parts.find(part => part.type === type).value).join('-')
}
export const previousDate = value => {
  const date = new Date(`${value.slice(0, 10)}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() - 1)
  return date.toISOString().slice(0, 10)
}
const formatDate = value => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(`${value}T12:00:00Z`))
export const parseWorkCalendarFocus = (search = '') => {
  const params = new URLSearchParams(search)
  const date = params.get('date')
  if (!date || !dateValid(date) || Number(date.slice(0, 4)) < 1000) return null
  const userId = params.get('userId') || ''
  return { date, userId: /^[a-f\d]{24}$/i.test(userId) ? userId.toLowerCase() : '' }
}
const ActivityRecordLink = ({ link }) => {
  if (!link) return null
  const safeHref = typeof link.href === 'string' && link.href.startsWith('/') && !link.href.startsWith('//') && !link.href.includes('\\')
  if (safeHref) return <Button component="a" href={link.href} target="_blank" rel="noopener noreferrer" size="small" color="primary" className={styles.recordLink} title="Opens in a new tab" aria-label={`${link.label} (opens in a new tab)`}>{link.label}</Button>
  return link.status === 'missing' ? <p className={styles.recordUnavailable}>Record no longer available</p> : null
}
const actionLabels = { 'work-day-saved': 'Work day saved', 'work-day-deleted': 'Work day removed' }
const humanize = value => actionLabels[value] || String(value || 'Activity').replace(/[_-]/g, ' ').replace(/^./, letter => letter.toUpperCase())
const valueText = value => typeof value === 'object' ? JSON.stringify(value) : String(value)
const activityDescription = row => {
  const metadata = row.metadata || {}
  const pieces = []
  if (row.entityType === 'work-day') {
    if (metadata.userName || metadata.userEmail) pieces.push(metadata.userName || metadata.userEmail)
    if (metadata.date && dateValid(metadata.date)) pieces.push(formatDate(metadata.date))
    if (metadata.dayType) pieces.push(dayLabel(metadata.dayType))
  }
  if (metadata.invoiceNumber) pieces.push(`Invoice #${metadata.invoiceNumber}`)
  if (metadata.quoteNumber) pieces.push(`Quote #${metadata.quoteNumber}`)
  if (metadata.orderNumber) pieces.push(`Order #${metadata.orderNumber}`)
  if (metadata.client) pieces.push(typeof metadata.client === 'string' ? metadata.client : metadata.client.store || metadata.client.name)
  if (metadata.address) pieces.push(valueText(metadata.address))
  if (metadata.amount != null) pieces.push(`Amount: $${Number(metadata.amount).toFixed(2)}`)
  else if (metadata.total != null) pieces.push(`Total: $${Number(metadata.total).toFixed(2)}`)
  if (metadata.status) pieces.push(`Status: ${metadata.status}`)
  return pieces.filter(Boolean).join(' · ') || `${humanize(row.entityType)}${row.entityId ? ` · ${row.entityId}` : ''}`
}

const ErrorNotice = ({ children, onRetry }) => <div className={styles.error} role="alert">{children}{onRetry && <Button size="small" onClick={onRetry}>Retry</Button>}</div>

const AuthorizedWorkCalendar = () => {
  const [today] = useState(easternToday)
  const [focus] = useState(() => parseWorkCalendarFocus(window.location.search))
  const focusPending = useRef(Boolean(focus?.userId))
  const focusedRow = useRef(null)
  const mobile = useMediaQuery('(max-width:600px)', { noSsr: true })
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [usersError, setUsersError] = useState('')
  const [entries, setEntries] = useState([])
  const [calendarRange, setCalendarRange] = useState(null)
  const [entriesLoading, setEntriesLoading] = useState(true)
  const [entriesError, setEntriesError] = useState('')
  const [revision, setRevision] = useState(0)
  const [notice, setNotice] = useState('')
  const [summaryStart, setSummaryStart] = useState(`${today.slice(0, 7)}-01`)
  const [summaryEnd, setSummaryEnd] = useState(today)
  const [summaryUser, setSummaryUser] = useState('')
  const [summary, setSummary] = useState([])
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState('')
  const [editor, setEditor] = useState(() => focus ? { date: focus.date } : null)
  const [editorEntries, setEditorEntries] = useState([])
  const [editorLoading, setEditorLoading] = useState(false)
  const [editorError, setEditorError] = useState('')
  const [editorDraft, setEditorDraft] = useState({})
  const [editorLoadedDate, setEditorLoadedDate] = useState('')
  const [editorRetry, setEditorRetry] = useState(0)
  const [editorSaveErrors, setEditorSaveErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState(null)
  const [selectedType, setSelectedType] = useState('full')
  const [activity, setActivity] = useState({ data: [], total: 0 })
  const [activityPage, setActivityPage] = useState(0)
  const [activityLoading, setActivityLoading] = useState(false)
  const [activityError, setActivityError] = useState('')
  const [mutationError, setMutationError] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const refresh = () => setRevision(value => value + 1)
  const summaryValid = dateValid(summaryStart) && dateValid(summaryEnd) && summaryStart <= summaryEnd

  useEffect(() => {
    let current = true
    setUsersLoading(true); setUsersError('')
    fetchWorkCalendarUsers().then(({ data }) => { if (current) setUsers(data.users || []) })
      .catch(error => { if (current) setUsersError(errorMessage(error, 'Could not load users.')) })
      .finally(() => { if (current) setUsersLoading(false) })
    return () => { current = false }
  }, [revision])

  useEffect(() => {
    if (!calendarRange) return undefined
    let current = true
    setEntriesLoading(true); setEntriesError(''); setEntries([])
    fetchWorkCalendarEntries(calendarRange).then(({ data }) => { if (current) setEntries(data.entries || []) })
      .catch(error => { if (current) setEntriesError(errorMessage(error, 'Could not load the work calendar.')) })
      .finally(() => { if (current) setEntriesLoading(false) })
    return () => { current = false }
  }, [calendarRange, revision])

  useEffect(() => {
    if (!summaryValid) { setSummary([]); setSummaryLoading(false); return undefined }
    let current = true
    setSummaryLoading(true); setSummaryError(''); setSummary([])
    fetchWorkCalendarSummary({ start: summaryStart, end: summaryEnd, ...(summaryUser ? { userId: summaryUser } : {}) })
      .then(({ data }) => { if (current) setSummary(data.rows || []) })
      .catch(error => { if (current) setSummaryError(errorMessage(error, 'Could not load work totals.')) })
      .finally(() => { if (current) setSummaryLoading(false) })
    return () => { current = false }
  }, [summaryStart, summaryEnd, summaryUser, summaryValid, revision])

  const editorDate = editor?.date
  useEffect(() => {
    if (!editorDate || !dateValid(editorDate)) {
      setEditorLoading(false); setEditorLoadedDate(''); setEditorError('')
      return undefined
    }
    let current = true
    setEditorLoading(true); setEditorError(''); setEditorEntries([])
    setEditorDraft({}); setEditorLoadedDate(''); setEditorSaveErrors({})
    fetchWorkCalendarEntries({ start: editorDate, end: editorDate }).then(({ data }) => {
      if (!current) return
      const saved = data.entries || []
      setEditorEntries(saved)
      setEditorDraft(Object.fromEntries(saved.map(entry => [entry.userId, entry.dayType])))
      setEditorLoadedDate(editorDate)
    })
      .catch(error => { if (current) setEditorError(errorMessage(error, 'Could not load this day.')) })
      .finally(() => { if (current) setEditorLoading(false) })
    return () => { current = false }
  }, [editorDate, editorRetry])

  useEffect(() => {
    if (!focusPending.current || usersLoading || usersError || editorLoading || editorError || editorLoadedDate !== focus?.date || editorDate !== focus?.date) return
    focusPending.current = false
    focusedRow.current?.focus()
  }, [focus, editorDate, editorLoadedDate, editorLoading, editorError, usersLoading, usersError])

  const selectedUserId = selected?.userId
  const selectedDate = selected?.date
  useEffect(() => {
    if (!selectedUserId || !selectedDate) return undefined
    let current = true
    setActivityLoading(true); setActivityError(''); setActivity({ data: [], total: 0 })
    fetchWorkCalendarActivity({ userId: selectedUserId, date: selectedDate, page: activityPage, limit: 25 })
      .then(({ data }) => { if (current) setActivity({ data: data.data || [], total: data.total || 0 }) })
      .catch(error => { if (current) setActivityError(errorMessage(error, 'Could not load recorded activity.')) })
      .finally(() => { if (current) setActivityLoading(false) })
    return () => { current = false }
  }, [selectedUserId, selectedDate, activityPage, revision])

  const datesSet = useCallback(({ startStr, endStr }) => {
    const next = { start: startStr.slice(0, 10), end: previousDate(endStr) }
    setCalendarRange(old => old?.start === next.start && old?.end === next.end ? old : next)
  }, [])
  const openEditor = date => {
    setNotice(''); setEditorError(''); setEditorSaveErrors({}); setEditorLoadedDate(''); setEditorDraft({})
    setEditor({ date })
  }
  const openActivity = entry => {
    setEditor(null); setSelected(entry); setSelectedType(entry.dayType); setActivityPage(0); setMutationError(''); setConfirmRemove(false)
  }
  // Keep former users' saved entries visible without inventing a new account.
  const editorUsers = [...users, ...editorEntries.filter(entry => !users.some(user => user.id === entry.userId)).map(entry => ({ id: entry.userId, name: entry.userName, email: entry.userEmail }))]
  const editorReady = editor && dateValid(editor.date) && editorLoadedDate === editor.date && !editorLoading && !editorError
  const editorChanges = editorReady ? editorUsers.map(user => {
    const entry = editorEntries.find(saved => saved.userId === user.id)
    return { user, entry, dayType: editorDraft[user.id] || '' }
  }).filter(change => (change.entry?.dayType || '') !== change.dayType) : []
  const toggleDay = (userId, dayType) => {
    setEditorDraft(value => ({ ...value, [userId]: value[userId] === dayType ? '' : dayType }))
    setEditorSaveErrors(value => { const next = { ...value }; delete next[userId]; return next })
  }
  const saveEditor = async () => {
    if (saving || !editorReady || usersLoading || usersError || !editorChanges.length) return
    const changes = editorChanges
    const date = editor.date
    setSaving(true); setEditorSaveErrors({})
    // A single Save day handles all changed rows. Keep successful rows as the new
    // baseline after a partial failure so retry only submits the remaining changes.
    const results = await Promise.allSettled(changes.map(change => change.dayType
      ? saveWorkCalendarEntry({ date, userId: change.user.id, dayType: change.dayType })
      : deleteWorkCalendarEntry(change.entry.id)))
    if (!mounted.current) return
    let savedEntries = [...editorEntries]
    const failures = {}
    results.forEach((result, index) => {
      const change = changes[index]
      if (result.status === 'rejected') {
        failures[change.user.id] = `${userLabel(change.user)}: ${errorMessage(result.reason, 'Could not save this change. Please try again.')}`
        return
      }
      savedEntries = savedEntries.filter(entry => entry.userId !== change.user.id)
      if (change.dayType) savedEntries.push(result.value.data.entry)
    })
    setEditorEntries(savedEntries)
    setEditorSaveErrors(failures)
    setSaving(false)
    if (results.some(result => result.status === 'fulfilled')) refresh()
    if (!Object.keys(failures).length) {
      setNotice(`Work day saved for ${formatDate(date)}.`)
      setEditor(null)
    }
  }
  const updateSelected = async () => {
    if (saving) return
    setSaving(true); setMutationError('')
    try {
      const { data } = await saveWorkCalendarEntry({ userId: selected.userId, date: selected.date, dayType: selectedType })
      if (!mounted.current) return
      setSelected(data.entry); setNotice('Work day updated.'); refresh()
    } catch (error) { if (mounted.current) setMutationError(errorMessage(error, 'Could not update this work day.')) }
    finally { if (mounted.current) setSaving(false) }
  }
  const removeSelected = async () => {
    if (saving) return
    setSaving(true); setMutationError('')
    try {
      await deleteWorkCalendarEntry(selected.id)
      if (!mounted.current) return
      setSelected(null); setConfirmRemove(false); setNotice('Work entry removed.'); refresh()
    } catch (error) { if (mounted.current) { setConfirmRemove(false); setMutationError(errorMessage(error, 'Could not remove this work entry.')) } }
    finally { if (mounted.current) setSaving(false) }
  }

  return <main className={styles.page}>
    <header className={styles.heading}>
      <div><h1>Work calendar</h1><p>Log full and half days, then select a person’s entry to review their recorded activity.</p></div>
      <Button variant="contained" color="primary" onClick={() => openEditor(today)}>Log work</Button>
    </header>
    <p className={styles.timeZone}>Work dates and activity use Eastern Time (America/New_York).</p>
    {notice && !editor && <div className={styles.success} role="status">{notice}</div>}
    {usersError && <ErrorNotice onRetry={refresh}>{usersError}</ErrorNotice>}
    <section className={styles.panel} aria-label="Work calendar">
      <div className={styles.calendarStatus} aria-live="polite">{entriesLoading ? 'Loading calendar…' : 'Select a day to log work. Select a person to see activity.'}</div>
      {entriesError && <ErrorNotice onRetry={refresh}>{entriesError}</ErrorNotice>}
      <div className={styles.calendar}>
        <FullCalendar plugins={[dayGridPlugin, interactionPlugin, listPlugin]} initialView={mobile ? 'listMonth' : 'dayGridMonth'} initialDate={focus?.date || today} now={today} timeZone="UTC" height="auto"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,listMonth' }} buttonText={{ today: 'Today', month: 'Month', list: 'List' }}
          datesSet={datesSet} dateClick={({ dateStr }) => openEditor(dateStr.slice(0, 10))} eventClick={({ event }) => openActivity(event.extendedProps.entry)}
          events={entries.map(entry => ({ id: entry.id, title: `${entry.userName || entry.userEmail} · ${dayLabel(entry.dayType)}`, start: entry.date, allDay: true, backgroundColor: entry.dayType === 'half' ? '#fff3dc' : '#e4efff', borderColor: entry.dayType === 'half' ? '#d2a858' : '#aac8ee', textColor: '#182c47', extendedProps: { entry } }))}
          eventInteractive={true} dayMaxEvents={false} displayEventTime={false} noEventsContent="No work logged this month. Use Log work to add a day." />
      </div>
      <div className={styles.legend}><span><i className={styles.fullDot} />Full day</span><span><i className={styles.halfDot} />Half day</span></div>
    </section>

    <section className={styles.panel} aria-labelledby="work-totals-heading">
      <h2 id="work-totals-heading">Work totals</h2>
      <p className={styles.muted}>Choose an inclusive date range. Equivalent days = full days + half days × 0.5.</p>
      <div className={styles.filters}>
        <label>Start date<input type="date" value={summaryStart} onChange={event => setSummaryStart(event.target.value)} /></label>
        <label>End date<input type="date" value={summaryEnd} onChange={event => setSummaryEnd(event.target.value)} /></label>
        <label>Person<select value={summaryUser} onChange={event => setSummaryUser(event.target.value)} disabled={usersLoading}><option value="">All users</option>{users.map(user => <option key={user.id} value={user.id}>{userLabel(user)}{user.name && user.email ? ` (${user.email})` : ''}</option>)}</select></label>
      </div>
      {!summaryValid ? <ErrorNotice>Choose valid dates with the end date on or after the start date.</ErrorNotice> : summaryError ? <ErrorNotice onRetry={refresh}>{summaryError}</ErrorNotice> : summaryLoading ? <p role="status">Loading work totals…</p> : <>
        <p className={styles.rangeCaption}>{formatDate(summaryStart)} – {formatDate(summaryEnd)} · both dates included</p>
        <div className={styles.tableWrap}><table className={styles.summaryTable}><thead><tr><th scope="col">Person</th><th scope="col">Full days</th><th scope="col">Half days</th><th scope="col">Equivalent days</th></tr></thead>
          <tbody>{summary.map(row => <tr key={row.userId}><th scope="row">{userLabel(row)}{row.email && row.name && <span>{row.email}</span>}</th><td>{row.fullDays}</td><td>{row.halfDays}</td><td>{row.totalDays}</td></tr>)}</tbody></table></div>
        {!summary.length && <p className={styles.empty}>No work logged for this date range.</p>}
      </>}
    </section>

    <Dialog open={Boolean(editor)} onClose={() => { if (!saving) setEditor(null) }} fullWidth maxWidth="sm" aria-labelledby="log-work-title">
      <DialogTitle id="log-work-title">Log work</DialogTitle><DialogContent className={styles.dialogContent}>
        {editor && <>
          <div className={styles.editorFields}>
            <label>Work date<input type="date" value={editor.date} disabled={saving} onChange={event => { setEditor({ date: event.target.value }); setNotice('') }} /></label>
          </div>
          <p className={styles.attendanceHelp}>Check a half day or full day for each employee. Leave both blank if they did not work. Clearing a saved selection removes that work entry when you save.</p>
          {!dateValid(editor.date) && <ErrorNotice>Choose a valid work date.</ErrorNotice>}
          {editorError && <ErrorNotice onRetry={() => setEditorRetry(value => value + 1)}>{editorError}</ErrorNotice>}
          {usersError && <ErrorNotice onRetry={refresh}>{usersError}</ErrorNotice>}
          {editorLoading || usersLoading ? <p role="status">Loading this day…</p> : editorReady && !usersError && <>
            {!editorUsers.length ? <p className={styles.empty}>No employees available.</p> : <table className={styles.attendanceTable}>
              <thead><tr><th scope="col">Employee</th><th scope="col">Half day</th><th scope="col">Full day</th></tr></thead>
              <tbody>{editorUsers.map(user => {
                const entry = editorEntries.find(saved => saved.userId === user.id)
                return <tr key={user.id} ref={focus?.userId === user.id ? focusedRow : null} tabIndex={-1}>
                  <th scope="row"><span>{userLabel(user)}</span>{entry && <button className={styles.activityShortcut} type="button" disabled={saving || editorChanges.length > 0} onClick={() => openActivity(entry)} aria-label={`View activity for ${userLabel(user)}`}>View activity</button>}{editorSaveErrors[user.id] && <p className={styles.rowError} role="alert">{editorSaveErrors[user.id]}</p>}</th>
                  {['half', 'full'].map(dayType => <td key={dayType}><label className={styles.dayChoice}><input type="checkbox" aria-label={`${userLabel(user)} — ${dayLabel(dayType)}`} checked={editorDraft[user.id] === dayType} disabled={saving} onChange={() => toggleDay(user.id, dayType)} /></label></td>)}
                </tr>
              })}</tbody>
            </table>}
            {Object.keys(editorSaveErrors).length > 0 && <p className={styles.muted}>Your remaining selections are kept. Save again to retry the changes that failed.</p>}
          </>}
        </>}
      </DialogContent><DialogActions><Button onClick={() => setEditor(null)} disabled={saving}>Cancel</Button><Button color="primary" variant="contained" onClick={saveEditor} disabled={saving || usersLoading || Boolean(usersError) || !editorReady || !editorChanges.length}>{saving ? 'Saving…' : 'Save day'}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(selected)} onClose={() => { if (!saving) setSelected(null) }} fullWidth maxWidth="md" aria-labelledby="work-activity-title">
      <DialogTitle id="work-activity-title">{selected?.userName || selected?.userEmail} · {selected ? formatDate(selected.date) : ''}</DialogTitle>
      <DialogContent className={styles.dialogContent}>{selected && <>
        <div className={styles.entryControls}><label>Recorded day<select value={selectedType} disabled={saving} onChange={event => setSelectedType(event.target.value)}><option value="full">Full day</option><option value="half">Half day</option></select></label><Button variant="outlined" color="primary" onClick={updateSelected} disabled={saving || selectedType === selected.dayType}>Update day</Button><Button className={styles.removeButton} onClick={() => setConfirmRemove(true)} disabled={saving}>Remove work entry</Button></div>
        {mutationError && <ErrorNotice>{mutationError}</ErrorNotice>}
        <h2>Recorded activity</h2><p className={styles.muted}>Recorded actions for this user on this work date, shown in Eastern Time. This includes actions tracked by the site; it is not a complete record of all work.</p>
        {activityLoading ? <p role="status">Loading recorded activity…</p> : activityError ? <ErrorNotice onRetry={refresh}>{activityError}</ErrorNotice> : <>
          {!activity.data.length ? <p className={styles.empty}>No recorded activity for this user on this day.</p> : <ol className={styles.activityList}>{activity.data.map((row, index) => <li key={row._id || row.id || `${row.createdAt}-${index}`}>
            <div className={styles.activityHeading}><strong>{humanize(row.action)}</strong><time dateTime={row.createdAt || undefined} title={formatActivityDateTime(row.createdAt)} aria-label={formatActivityDateTime(row.createdAt)}>{formatActivityTime(row.createdAt)}</time></div><p>{activityDescription(row)}</p><ActivityRecordLink link={row.recordLink} />
            <details><summary>Recorded details</summary><dl className={styles.metadata}><dt>Recorded at</dt><dd>{formatActivityDateTime(row.createdAt)}</dd>{Object.entries(row.metadata || {}).map(([key, value]) => <React.Fragment key={key}><dt>{humanize(key)}</dt><dd>{formatActivityValue(value, key)}</dd></React.Fragment>)}</dl></details>
          </li>)}</ol>}
          <div className={styles.pagination}><span>{activity.total ? `${activityPage * 25 + 1}–${Math.min((activityPage + 1) * 25, activity.total)} of ${activity.total} actions` : '0 actions'}</span><Button disabled={activityPage === 0} onClick={() => setActivityPage(page => page - 1)}>Previous activity</Button><Button disabled={(activityPage + 1) * 25 >= activity.total} onClick={() => setActivityPage(page => page + 1)}>Next activity</Button></div>
        </>}
      </>}</DialogContent><DialogActions><Button disabled={saving} onClick={() => setSelected(null)}>Close</Button></DialogActions>
    </Dialog>
    <Dialog open={confirmRemove} onClose={() => { if (!saving) setConfirmRemove(false) }} aria-labelledby="remove-work-title" maxWidth="xs" fullWidth><DialogTitle id="remove-work-title">Remove work entry?</DialogTitle><DialogContent><p>Remove {selected?.userName || selected?.userEmail}’s {selected ? dayLabel(selected.dayType).toLowerCase() : ''} on {selected ? formatDate(selected.date) : ''} from the calendar and work totals?</p><p>Recorded activity will remain available in the activity log.</p></DialogContent><DialogActions><Button disabled={saving} onClick={() => setConfirmRemove(false)}>Cancel</Button><Button disabled={saving} color="secondary" onClick={removeSelected}>{saving ? 'Removing…' : 'Remove entry'}</Button></DialogActions></Dialog>
  </main>
}

const WorkCalendarPage = () => {
  const auth = useAuthUser()
  if ((auth?.result?.email || '').trim().toLowerCase() !== adminEmail) return <main className={styles.page}><h1>Work calendar</h1><p role="alert">This page is available to the administrator only.</p></main>
  return <AuthorizedWorkCalendar />
}
export default WorkCalendarPage
